import { useState } from 'react';
import {
  Avatar,
  Breadcrumb,
  Button,
  Dropdown,
  Layout,
  Menu,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ApartmentOutlined,
  BankOutlined,
  BookOutlined,
  DownOutlined,
  GlobalOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const { Sider, Header, Content } = Layout;

type NavItem = { key: string; icon: JSX.Element; label: string };

/** Grouped so the rail reads as two jobs rather than five links: who has access
 *  to what, and how the platform itself is configured. */
const NAV_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Directory',
    items: [
      { key: '/users', icon: <TeamOutlined />, label: 'Users' },
      { key: '/organizations', icon: <BankOutlined />, label: 'Organizations' },
      { key: '/workspaces', icon: <ApartmentOutlined />, label: 'Workspaces' },
      // Belongs beside the directory it grants against, not under Platform:
      // it answers "who may do what", which is this group's whole job.
      { key: '/accesses', icon: <KeyOutlined />, label: 'Accesses' },
    ],
  },
  {
    title: 'Platform',
    items: [{ key: '/tenants', icon: <GlobalOutlined />, label: 'Tenants' }],
  },
  // Its own group at the foot of the rail: it is not a thing you administer, and
  // filing it under Directory would put a reference page among the screens it
  // describes.
  {
    title: 'Reference',
    items: [{ key: '/documentation', icon: <BookOutlined />, label: 'Documentation' }],
  },
];

const NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

const toMenuItem = (item: NavItem) => ({
  key: item.key,
  icon: item.icon,
  label: <Link to={item.key}>{item.label}</Link>,
});

// Localhost is the one environment worth labelling: a badge on production would
// be noise, but mistaking local data for the real thing is a costly mistake.
const IS_LOCAL = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const { email, logout } = useAuth();
  const location = useLocation();

  // Highlight by path prefix so a nested route keeps its parent item selected.
  const current = NAV_ITEMS.find((item) => location.pathname.startsWith(item.key));
  const selected = current?.key ?? '/users';
  const section = NAV_GROUPS.find((group) => group.items.some((i) => i.key === selected));

  return (
    <Layout className="app-shell">
      <Sider
        className="app-rail"
        collapsible
        collapsed={collapsed}
        trigger={null}
        width={232}
        collapsedWidth={64}
        theme="dark"
      >
        <div className="app-brand">
          <span className="app-brand-mark">S</span>
          {!collapsed && (
            <span>
              <div className="app-brand-name">Seiki</div>
              <div className="app-brand-sub">Backoffice</div>
            </span>
          )}
        </div>

        <div className="app-nav">
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selected]}
            // Collapsed to icons the group headings would be blank rows, so the
            // rail flattens to a single list at that width.
            items={
              collapsed
                ? NAV_ITEMS.map(toMenuItem)
                : NAV_GROUPS.map((group) => ({
                    key: group.title,
                    type: 'group' as const,
                    label: group.title,
                    children: group.items.map(toMenuItem),
                  }))
            }
          />
        </div>

        <div className="app-rail-foot">{collapsed ? 'v0.1' : 'Seiki staff console · v0.1'}</div>
      </Sider>

      <Layout>
        <Header className="app-header">
          <div className="app-header-left">
            <Button
              type="text"
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((value) => !value)}
            />
            <Breadcrumb
              items={[
                { title: section?.title ?? 'Backoffice' },
                { title: <span style={{ color: 'var(--bo-text)' }}>{current?.label ?? ''}</span> },
              ]}
            />
          </div>

          <div className="app-header-right">
            {IS_LOCAL && (
              <Tooltip title="You are pointed at a local backend, not production">
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  Local
                </Tag>
              </Tooltip>
            )}
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'identity',
                    label: (
                      <div style={{ padding: '2px 0' }}>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          Signed in as
                        </Typography.Text>
                        <div style={{ fontSize: 13 }}>{email}</div>
                      </div>
                    ),
                    disabled: true,
                  },
                  { type: 'divider' },
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: 'Sign out',
                    danger: true,
                    onClick: logout,
                  },
                ],
              }}
            >
              <div className="app-user">
                <Avatar size={28} style={{ background: 'var(--bo-navy)', fontSize: 12 }}>
                  {(email ?? '?').slice(0, 1).toUpperCase()}
                </Avatar>
                <span className="app-user-email">{email}</span>
                <DownOutlined style={{ fontSize: 10, color: 'var(--bo-muted)' }} />
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="app-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
